import React, { useState, useEffect, useCallback } from 'react';
import {
  Pause, Play, Plus, Target, Trash2, TrendingUp, Trophy,
} from 'lucide-react';
import { CTA_LABELS, VARIANT_COLORS } from './_constants';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ABTestingPanel() {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newTest, setNewTest] = useState({ name: '', target_cta: 'explore_peptides', min_clicks: 200, confidence_threshold: 95, variants: [
    { id: 'a', label: 'Variant A', config: { text: '', color: '#E63946' } },
    { id: 'b', label: 'Variant B', config: { text: '', color: '#3b82f6' } },
  ]});

  const fetchTests = useCallback(async () => {
    try {
      const token = sessionStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/admin/ab-tests`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setTests(data || []);
    } catch { setTests([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTests(); }, [fetchTests]);

  const createTest = async () => {
    if (!newTest.name || newTest.variants.some(v => !v.config.text)) return;
    try {
      const token = sessionStorage.getItem('admin_token');
      await fetch(`${API_URL}/api/admin/ab-tests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(newTest),
      });
      setShowCreate(false);
      setNewTest({ name: '', target_cta: 'explore_peptides', min_clicks: 200, confidence_threshold: 95, variants: [
        { id: 'a', label: 'Variant A', config: { text: '', color: '#E63946' } },
        { id: 'b', label: 'Variant B', config: { text: '', color: '#3b82f6' } },
      ]});
      fetchTests();
    } catch {}
  };

  const updateStatus = async (testId, status, winner = null) => {
    try {
      const token = sessionStorage.getItem('admin_token');
      const body = { status };
      if (winner) body.winner = winner;
      await fetch(`${API_URL}/api/admin/ab-tests/${testId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      fetchTests();
    } catch {}
  };

  const deleteTest = async (testId) => {
    try {
      const token = sessionStorage.getItem('admin_token');
      await fetch(`${API_URL}/api/admin/ab-tests/${testId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchTests();
    } catch {}
  };

  const addVariant = () => {
    const nextId = String.fromCharCode(97 + newTest.variants.length); // c, d, e...
    setNewTest(prev => ({
      ...prev,
      variants: [...prev.variants, {
        id: nextId,
        label: `Variant ${nextId.toUpperCase()}`,
        config: { text: '', color: VARIANT_COLORS[prev.variants.length % VARIANT_COLORS.length] }
      }]
    }));
  };

  const removeVariant = (idx) => {
    if (newTest.variants.length <= 2) return;
    setNewTest(prev => ({ ...prev, variants: prev.variants.filter((_, i) => i !== idx) }));
  };

  const updateVariant = (idx, field, value) => {
    setNewTest(prev => {
      const variants = [...prev.variants];
      if (field === 'text' || field === 'color') {
        variants[idx] = { ...variants[idx], config: { ...variants[idx].config, [field]: value } };
      } else {
        variants[idx] = { ...variants[idx], [field]: value };
      }
      return { ...prev, variants };
    });
  };

  return (
    <div className="space-y-6" data-testid="ab-testing-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-white text-lg font-semibold" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
          A/B Testing
        </h3>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="px-4 py-2 text-xs uppercase tracking-wider bg-[#E63946] text-white hover:opacity-90 transition-opacity"
          style={{ fontFamily: 'Bebas Neue, sans-serif' }}
          data-testid="create-ab-test-btn"
        >
          <Plus className="w-3 h-3 inline mr-1" />
          {showCreate ? 'Cancel' : 'New Test'}
        </button>
      </div>

      {/* Create Form */}
      {showCreate && (
        <div className="bg-zinc-900 border border-zinc-800 p-6 space-y-4" data-testid="ab-test-create-form">
          <div>
            <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1">Test Name</label>
            <input
              className="w-full bg-zinc-800 border border-zinc-700 text-white px-3 py-2 text-sm"
              placeholder="e.g., Hero Primary CTA Text"
              value={newTest.name}
              onChange={e => setNewTest(p => ({ ...p, name: e.target.value }))}
              data-testid="ab-test-name-input"
            />
          </div>
          <div>
            <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1">Target CTA</label>
            <select
              className="w-full bg-zinc-800 border border-zinc-700 text-white px-3 py-2 text-sm"
              value={newTest.target_cta}
              onChange={e => setNewTest(p => ({ ...p, target_cta: e.target.value }))}
              data-testid="ab-test-cta-select"
            >
              {Object.entries(CTA_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-zinc-400 text-xs uppercase tracking-wider">Variants</label>
              <button onClick={addVariant} className="text-[#E63946] text-xs hover:text-white transition-colors">
                <Plus className="w-3 h-3 inline" /> Add Variant
              </button>
            </div>
            {newTest.variants.map((v, i) => (
              <div key={v.id} className="flex items-center gap-2 mb-2">
                <span className="w-6 h-6 rounded-full shrink-0 flex items-center justify-center text-white text-[10px] font-bold" style={{ backgroundColor: v.config.color }}>
                  {v.id.toUpperCase()}
                </span>
                <input
                  className="flex-1 bg-zinc-800 border border-zinc-700 text-white px-3 py-1.5 text-sm"
                  placeholder={`CTA text for variant ${v.id.toUpperCase()}`}
                  value={v.config.text}
                  onChange={e => updateVariant(i, 'text', e.target.value)}
                  data-testid={`variant-${v.id}-text`}
                />
                <input
                  type="color"
                  className="w-8 h-8 bg-transparent border border-zinc-700 cursor-pointer"
                  value={v.config.color}
                  onChange={e => updateVariant(i, 'color', e.target.value)}
                />
                {newTest.variants.length > 2 && (
                  <button onClick={() => removeVariant(i)} className="text-zinc-600 hover:text-red-500 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Auto-completion thresholds */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1">Min Clicks for Auto-Complete</label>
              <input
                type="number"
                className="w-full bg-zinc-800 border border-zinc-700 text-white px-3 py-2 text-sm"
                value={newTest.min_clicks}
                onChange={e => setNewTest(p => ({ ...p, min_clicks: parseInt(e.target.value) || 200 }))}
                data-testid="ab-test-min-clicks"
              />
            </div>
            <div>
              <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1">Confidence Threshold (%)</label>
              <input
                type="number"
                className="w-full bg-zinc-800 border border-zinc-700 text-white px-3 py-2 text-sm"
                value={newTest.confidence_threshold}
                min={80}
                max={99}
                onChange={e => setNewTest(p => ({ ...p, confidence_threshold: parseInt(e.target.value) || 95 }))}
                data-testid="ab-test-confidence-threshold"
              />
            </div>
          </div>
          <p className="text-zinc-600 text-[10px]">Test auto-completes when both thresholds are met. You can still declare a winner manually at any time.</p>

          <button
            onClick={createTest}
            className="w-full py-2.5 text-sm uppercase tracking-wider bg-[#E63946] text-white hover:opacity-90 transition-opacity"
            style={{ fontFamily: 'Bebas Neue, sans-serif' }}
            data-testid="ab-test-submit-btn"
          >
            Launch A/B Test
          </button>
        </div>
      )}

      {/* Existing Tests */}
      {loading ? (
        <div className="text-zinc-500 text-center py-8">Loading tests...</div>
      ) : tests.length === 0 ? (
        <div className="bg-zinc-900 border border-zinc-800 p-8 text-center">
          <Target className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-500 text-sm">No A/B tests yet. Create one to start split-testing your CTAs.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {tests.map(test => {
            const maxV = Math.max(...test.variants.map(v => v.clicks || 0), 1);
            return (
              <div key={test.id} className="bg-zinc-900 border border-zinc-800 p-5" data-testid={`ab-test-card-${test.id}`}>
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="text-white text-base font-semibold">{test.name}</h4>
                    <p className="text-zinc-500 text-xs">
                      Target: <span className="text-zinc-300">{CTA_LABELS[test.target_cta] || test.target_cta}</span>
                      <span className="mx-2">|</span>
                      {test.total_clicks} total clicks
                      <span className="mx-2">|</span>
                      Min: {test.min_clicks || 200} clicks @ {test.confidence_threshold || 95}% confidence
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {test.auto_completed && (
                      <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold bg-[#c8ff00]/10 text-[#c8ff00] border border-[#c8ff00]/30 rounded-full" data-testid="auto-completed-badge">
                        Auto-Completed
                      </span>
                    )}
                    <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold rounded-full ${
                      test.status === 'active' ? 'bg-green-500/10 text-green-400 border border-green-500/30' :
                      test.status === 'paused' ? 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/30' :
                      'bg-zinc-500/10 text-zinc-400 border border-zinc-500/30'
                    }`} data-testid="ab-test-status">
                      {test.status}
                    </span>
                    {test.winner && (
                      <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold bg-[#E63946]/10 text-[#E63946] border border-[#E63946]/30 rounded-full">
                        Winner: {test.winner.toUpperCase()}
                      </span>
                    )}
                  </div>
                </div>

                {/* Confidence Meter + Sample Progress */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  {/* Confidence */}
                  <div className="bg-zinc-800/50 p-3 border border-zinc-800" data-testid="confidence-meter">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-zinc-500 text-[10px] uppercase tracking-wider">Confidence</span>
                      <span className={`text-sm font-bold ${
                        (test.significance?.confidence || 0) >= 95 ? 'text-[#c8ff00]' :
                        (test.significance?.confidence || 0) >= 80 ? 'text-yellow-400' :
                        'text-zinc-400'
                      }`} data-testid="confidence-value">
                        {test.auto_completed ? (test.final_confidence || test.significance?.confidence || 0) : (test.significance?.confidence || 0)}%
                      </span>
                    </div>
                    <div className="h-2 bg-zinc-900 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-700"
                        style={{
                          width: `${Math.min(100, test.auto_completed ? (test.final_confidence || 0) : (test.significance?.confidence || 0))}%`,
                          backgroundColor: (test.significance?.confidence || 0) >= 95 ? '#c8ff00' :
                            (test.significance?.confidence || 0) >= 80 ? '#f59e0b' : '#71717a',
                        }}
                      />
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-zinc-600 text-[9px]">p = {test.auto_completed ? (test.final_p_value ?? 'N/A') : (test.significance?.p_value ?? 'N/A')}</span>
                      <span className="text-zinc-600 text-[9px]">
                        {(test.significance?.confidence || 0) >= (test.confidence_threshold || 95) ? 'Significant' :
                         (test.significance?.confidence || 0) >= 80 ? 'Approaching' : 'Collecting data'}
                      </span>
                    </div>
                  </div>

                  {/* Sample Progress */}
                  <div className="bg-zinc-800/50 p-3 border border-zinc-800" data-testid="sample-progress">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-zinc-500 text-[10px] uppercase tracking-wider">Sample Size</span>
                      <span className="text-zinc-300 text-sm font-bold">
                        {test.total_clicks} / {test.min_clicks || 200}
                      </span>
                    </div>
                    <div className="h-2 bg-zinc-900 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-700 bg-blue-500"
                        style={{ width: `${test.sample_progress_pct || 0}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-zinc-600 text-[9px]">{test.sample_progress_pct || 0}% complete</span>
                      <span className="text-zinc-600 text-[9px]">
                        {(test.sample_progress_pct || 0) >= 100 ? 'Threshold met' : `${(test.min_clicks || 200) - test.total_clicks} more needed`}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Variant bars */}
                <div className="space-y-3 mb-4">
                  {test.variants.map((v, i) => {
                    const pct = maxV > 0 ? (v.clicks / maxV) * 100 : 0;
                    const isLeader = test.leader === v.id;
                    const color = v.config?.color || VARIANT_COLORS[i % VARIANT_COLORS.length];
                    return (
                      <div key={v.id} data-testid={`ab-variant-bar-${v.id}`}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <span className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[9px] font-bold shrink-0" style={{ backgroundColor: color }}>
                              {v.id.toUpperCase()}
                            </span>
                            <span className="text-zinc-300 text-sm">{v.config?.text || v.label}</span>
                            {isLeader && <TrendingUp className="w-3.5 h-3.5 text-green-400" />}
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-white text-sm font-bold">{v.clicks || 0}</span>
                            <span className="text-zinc-500 text-xs">{v.click_rate || 0}%</span>
                          </div>
                        </div>
                        <div className="h-2.5 bg-zinc-800 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-700"
                            style={{ width: `${pct}%`, backgroundColor: color }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 pt-3 border-t border-zinc-800">
                  {test.status === 'active' && (
                    <button
                      onClick={() => updateStatus(test.id, 'paused')}
                      className="px-3 py-1.5 text-[10px] uppercase tracking-wider border border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10 transition-colors"
                    >
                      <Pause className="w-3 h-3 inline mr-1" /> Pause
                    </button>
                  )}
                  {test.status === 'paused' && (
                    <button
                      onClick={() => updateStatus(test.id, 'active')}
                      className="px-3 py-1.5 text-[10px] uppercase tracking-wider border border-green-500/30 text-green-400 hover:bg-green-500/10 transition-colors"
                    >
                      <Play className="w-3 h-3 inline mr-1" /> Resume
                    </button>
                  )}
                  {test.status !== 'completed' && test.leader && (
                    <button
                      onClick={() => updateStatus(test.id, 'completed', test.leader)}
                      className="px-3 py-1.5 text-[10px] uppercase tracking-wider border border-[#E63946]/30 text-[#E63946] hover:bg-[#E63946]/10 transition-colors"
                      data-testid="declare-winner-btn"
                    >
                      <Trophy className="w-3 h-3 inline mr-1" /> Declare {test.leader.toUpperCase()} Winner
                    </button>
                  )}
                  <button
                    onClick={() => deleteTest(test.id)}
                    className="px-3 py-1.5 text-[10px] uppercase tracking-wider border border-zinc-700 text-zinc-500 hover:text-red-400 hover:border-red-500/30 transition-colors ml-auto"
                  >
                    <Trash2 className="w-3 h-3 inline mr-1" /> Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
